import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../fonts/knswami.css';
import {
  Button,
  Card,
  ConfigProvider,
  Input,
  InputNumber,
  List,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthContext';
import { SUBANTA_LINGA, enumToOptions } from '../constants/subantaEnums';
import {
  searchSubantas,
  type Subanta,
  type SubantaLinga,
  type SubantaSearchMode,
} from '../api/subantaApi';
import { searchTingantas, type Tinganta, type TingantaSearchMode } from '../api/tingantaApi';

type SearchKind = 'subanta' | 'tinganta';

function errMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || fallback;
}

const MODE_OPTIONS: { value: SubantaSearchMode; label: string }[] = [
  { value: 'similar', label: 'Similar' },
  { value: 'exact', label: 'Exact' },
];

const LINGA_OPTIONS = enumToOptions(SUBANTA_LINGA);

export function DashboardHome() {
  const { user } = useAuth();
  const [kind, setKind] = useState<SearchKind>('subanta');
  const [word, setWord] = useState('');
  const [mode, setMode] = useState<SubantaSearchMode | undefined>(undefined);
  const [linga, setLinga] = useState<SubantaLinga | undefined>(undefined);
  const [limit, setLimit] = useState<number | undefined>(20);

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [subantaResults, setSubantaResults] = useState<Subanta[]>([]);
  const [tingantaResults, setTingantaResults] = useState<Tinganta[]>([]);

  const trimmedWord = word.trim();

  const runSearch = async () => {
    if (!trimmedWord) {
      message.warning('Enter a word to search');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      if (kind === 'subanta') {
        const rows = await searchSubantas({
          word: trimmedWord,
          mode,
          limit,
          linga,
        });
        setSubantaResults(rows);
        setTingantaResults([]);
      } else {
        const rows = await searchTingantas({
          word: trimmedWord,
          mode: mode as TingantaSearchMode | undefined,
          limit,
        });
        setTingantaResults(rows);
        setSubantaResults([]);
      }
    } catch (err: unknown) {
      message.error(errMessage(err, 'Search failed'));
      setSubantaResults([]);
      setTingantaResults([]);
    } finally {
      setLoading(false);
    }
  };

  const results = kind === 'subanta' ? subantaResults : tingantaResults;
  const resultCount = results.length;

  return (
    <ConfigProvider theme={{ token: { fontFamily: "'Knswami', sans-serif" } }}>
      <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 960 }}>
        <Card>
          <Space direction="vertical" size={6}>
            <Typography.Title level={2} style={{ margin: 0 }}>
              Welcome{user ? `, ${user.username}` : ''}.
            </Typography.Title>
            <Typography.Text type="secondary">
              Search subanta or tinganta entries from the dictionary API.
            </Typography.Text>
          </Space>
        </Card>

        <Card title="Dictionary search">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap style={{ width: '100%' }}>
              <Select<SearchKind>
                value={kind}
                onChange={(v) => {
                  setKind(v);
                  setLinga(undefined);
                }}
                options={[
                  { value: 'subanta', label: 'Subanta' },
                  { value: 'tinganta', label: 'Tinganta' },
                ]}
                style={{ width: 140 }}
              />
              <Input
                value={word}
                onChange={(e) => setWord(e.target.value)}
                onPressEnter={() => void runSearch()}
                placeholder={kind === 'subanta' ? 'e.g. राम or रामः' : 'e.g. भू or dhatu…'}
                aria-label="Search word"
                style={{ flex: '1 1 200px', minWidth: 200, maxWidth: 480 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={loading}
                disabled={!trimmedWord}
                onClick={() => void runSearch()}
              >
                Search
              </Button>
            </Space>

            <Space wrap style={{ width: '100%' }}>
              <Space size={4}>
                <Typography.Text type="secondary">Mode</Typography.Text>
                <Select<SubantaSearchMode | undefined>
                  allowClear
                  placeholder="Default"
                  value={mode}
                  onChange={setMode}
                  options={MODE_OPTIONS}
                  style={{ width: 120 }}
                />
              </Space>
              {kind === 'subanta' ? (
                <Space size={4}>
                  <Typography.Text type="secondary">Linga</Typography.Text>
                  <Select<SubantaLinga | undefined>
                    allowClear
                    placeholder="Any"
                    value={linga}
                    onChange={setLinga}
                    options={LINGA_OPTIONS}
                    style={{ width: 180 }}
                  />
                </Space>
              ) : null}
              <Space size={4}>
                <Typography.Text type="secondary">Limit</Typography.Text>
                <InputNumber
                  min={1}
                  max={200}
                  value={limit}
                  onChange={(v) => setLimit(v ?? undefined)}
                  style={{ width: 88 }}
                />
              </Space>
            </Space>
          </Space>

          <div style={{ marginTop: 16 }}>
            {loading ? (
              <Typography.Text type="secondary">Searching…</Typography.Text>
            ) : !searched ? (
              <Typography.Text type="secondary">
                Choose Subanta or Tinganta, enter a word, and press Search.
              </Typography.Text>
            ) : resultCount === 0 ? (
              <Typography.Text type="secondary">No matches.</Typography.Text>
            ) : kind === 'subanta' ? (
              <List<Subanta>
                itemLayout="vertical"
                dataSource={subantaResults}
                renderItem={(row) => (
                  <List.Item style={{ paddingBlock: 10 }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space wrap align="center">
                        <Typography.Text strong style={{ fontSize: 18 }}>
                          {row.word}
                        </Typography.Text>
                        {row.iast ? (
                          <Typography.Text type="secondary">{row.iast}</Typography.Text>
                        ) : null}
                        <Tag>#{row.id}</Tag>
                      </Space>
                      <Space wrap size={[4, 4]}>
                        {row.linga ? <Tag>{row.linga}</Tag> : null}
                        {row.prakar ? <Tag>{row.prakar}</Tag> : null}
                        {row.bachan != null ? <Tag>bachan {row.bachan}</Tag> : null}
                      </Space>
                      {row.base_word && row.base_word !== row.word ? (
                        <Typography.Text type="secondary">Base: {row.base_word}</Typography.Text>
                      ) : null}
                      <Link to="/dashboard/subantas">Open Subanta admin →</Link>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <List<Tinganta>
                itemLayout="vertical"
                dataSource={tingantaResults}
                renderItem={(row) => (
                  <List.Item style={{ paddingBlock: 10 }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space wrap align="center">
                        <Typography.Text strong style={{ fontSize: 16 }}>
                          {row.dhatu || row.aupadeshik || row.i || '—'}
                        </Typography.Text>
                        <Tag>#{row.id}</Tag>
                      </Space>
                      {row.aupadeshik ? (
                        <Typography.Text type="secondary">Aupadeshik: {row.aupadeshik}</Typography.Text>
                      ) : null}
                      {row.artha ? (
                        <Typography.Paragraph style={{ margin: 0 }}>{row.artha}</Typography.Paragraph>
                      ) : null}
                      {row.artha_english ? (
                        <Typography.Text type="secondary">{row.artha_english}</Typography.Text>
                      ) : null}
                      <Space wrap size={[4, 4]}>
                        {row.gana ? <Tag>gana {row.gana}</Tag> : null}
                        {row.pada ? <Tag>pada {row.pada}</Tag> : null}
                      </Space>
                      <Link to="/dashboard/tingantas">Open Tinganta admin →</Link>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </div>
        </Card>
      </Space>
    </ConfigProvider>
  );
}
